import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import {
  FiHome, FiUsers, FiCalendar, FiMail, FiBarChart2, FiSettings,
  FiMoon, FiSun, FiLogOut, FiEdit3, FiClipboard, FiCheckSquare, FiChevronLeft
} from 'react-icons/fi';
import './Layout.css';

const SIDEBAR_COLLAPSED_KEY = 'tvet_sidebar_collapsed';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { path: '/', label: 'Dashboard', icon: FiHome },
    ],
  },
  {
    label: 'Trainee Management',
    items: [
      { path: '/trainees', label: 'Trainees', icon: FiUsers },
      { path: '/batches', label: 'Batches', icon: FiSettings },
      { path: '/attendance', label: 'Attendance', icon: FiCalendar },
      { path: '/profile-links', label: 'Update Links', icon: FiEdit3 },
      { path: '/evaluations', label: 'Evaluations', icon: FiClipboard },
      { path: '/assessment', label: 'Assessment', icon: FiCheckSquare },
    ],
  },
  {
    label: 'Engage',
    items: [
      { path: '/communication', label: 'Communication', icon: FiMail },
      { path: '/analytics', label: 'Analytics', icon: FiBarChart2 },
    ],
  },
];

const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

const Layout = ({ children }) => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { logout, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true');
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const isActive = (path) => location.pathname === path;
  const currentItem = ALL_NAV_ITEMS.find((i) => isActive(i.path));
  const pageTitle = currentItem?.label || 'Dashboard';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initials = (user?.username || 'A').slice(0, 2).toUpperCase();

  return (
    <div className="layout" data-theme={theme}>
      <header className="header">
        <div className="header-content">
          <div className="header-titles">
            <h1>{pageTitle}</h1>
            <p className="header-breadcrumb">CSS FARMS IDEAS-TVET / {pageTitle}</p>
          </div>
          <div className="header-actions">
            <button onClick={toggleTheme} className="theme-toggle" title="Toggle theme">
              {theme === 'light' ? <FiMoon /> : <FiSun />}
            </button>
            <button onClick={handleLogout} className="logout-btn" title="Logout">
              <FiLogOut /> <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="layout-body">
        <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-brand">
            <div className="logo-container">
              <img
                src="/logo/css-farms-tvet-logo.png"
                alt="CSS FARMS IDEAS-TVET Logo"
                className="logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="logo-text">
              <h1>CSS FARMS IDEAS-TVET</h1>
              <p>Trainee Management System</p>
            </div>
            <button
              className="sidebar-collapse-btn"
              onClick={toggleCollapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <FiChevronLeft />
            </button>
          </div>

          <nav className="nav">
            {NAV_GROUPS.map((group) => (
              <div className="nav-group" key={group.label}>
                <span className="nav-group-label">{group.label}</span>
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon /> <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            ))}
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-account">
              <div className="sidebar-avatar">{initials}</div>
              <div className="sidebar-account-info">
                <strong>{user?.username || 'Admin'}</strong>
                <span>{user?.role === 'admin' ? 'Administrator' : (user?.role || 'Admin')}</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
